import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import styles from './browser.module.scss';
import { fetchStreamChildren, fetchStreams } from "src/redux/actions/streams";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Reveal from "src/components/animation";
import InfiniteScroll from "src/components/infiniteScroll";
import { useSpring, animated } from "@react-spring/web";
import { Stream } from "src/types/streams";
import { Collection, CollectionSection } from "src/types/collections";
import useMeasure from "react-use-measure";
import { useLocation, useParams } from "react-router-dom";

import Tooltip from "src/components/tooltip";
import { fetchGroupChildren } from "src/redux/actions/groups";
import Text from "src/components/text";

interface EntityBrowserProps {
  className?: string;
  width: number;
  entity?: Stream | Collection | CollectionSection;
  items?: any[];
  loading?: boolean;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  moveTo?: Stream;
  copyTo?: Stream[];
}

const EntityBrowser: React.FC<EntityBrowserProps> = ({ className, width, items, loading, action, onSelection, moveTo, copyTo }) => {

  const params = useParams();
  const location = useLocation();
  const pathName = _.get(location, "pathname");
  const groupItems = items?.filter((item) => item.entityType === 'group');

  return (
    <div className={cx(styles.container, className)}>
      {items && items.map((item) => (
        <EntityRow key={item.id} entity={item} width={width} action={action} onSelection={onSelection} moveTo={moveTo} copyTo={copyTo}/>
      ))}
      {!loading && _.isEmpty(items) && (<span>No entities found</span>)}
    </div>
  );
}


const StreamsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection, moveTo, copyTo }) => {
  const dispatch = useDispatch();
  const { loading, streams } = useSelector((state: any) => {

    const streams = _.get(state, 'hub.streams');
    const filteredStreams = (_.get(streams, 'items') || []).filter((item: any) => {
      return item.canEdit;
    });

    
    return {
      loading: state.hub.streams.loading,
      streams: filteredStreams,
    };
  }, _.isEqual);

  useEffect(() => {
    dispatch(fetchStreams());
  }, []);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        <StreamPlaceholder width={'50%'} />
        <StreamPlaceholder width={'70%'} />
        <StreamPlaceholder width={'60%'} />
        <StreamPlaceholder width={'50%'} />
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser className={className} loading={loading} items={streams} width={width} action={action} onSelection={onSelection} moveTo={moveTo} copyTo={copyTo} />
      </Reveal>
    </div>
  );
}

const StreamBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection, moveTo, copyTo }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.stream.items[entity!.id!]?.loadingChildren,
      items: state.stream.items[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchStreamChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser className={className} loading={loading} width={width} items={items} action={action} onSelection={onSelection} moveTo={moveTo} copyTo={copyTo} />
      </InfiniteScroll>
    </div>
  );
}


/**
 * Entity Renders
*/

interface EntityRowProps {
  entity: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  moveTo?: Stream;
  copyTo?: Stream[];
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection, moveTo, copyTo }) => {
  const Component = useMemo(() => {
    const type = entity?.entityType;
    switch (type) {
      case 'stream':
        return StreamRow;
      case 'group':
        return GroupRow;
      default:
        return false;
    }
  }, [entity]);


  if (!Component) {
    return null;
  }

  return (
    React.createElement(Component, { entity, width, action, onSelection, moveTo, copyTo })
  );
}

const StreamRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection, moveTo, copyTo }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const location = useLocation();
  const pathName = _.get(location, "pathname");

  const sourceId = pathName.split('/').pop();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleExpand = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  const handleClick = () => {
    if(entity.id === sourceId) {
      return; 
    } else {
      onSelection(entity);
    }
  }

  const isSelected = () => {
    if(copyTo && _.size(copyTo) > 0) {
      return copyTo.some((item) => item.id === entity.id);
    } else {
      return moveTo && moveTo.id === entity.id
    }
  }

  const isDisabled = () => {
    if(copyTo && _.size(copyTo) > 0){
      return copyTo.some((item) => item.id === sourceId);
    } else {
      return entity.id === sourceId
    }
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, { [styles.expanded]: expanded })} style={{ width: width }} onClick={handleExpand}>

        <div className={cx(styles.name, {
           [styles.selectedEntity]: isSelected(),
           [styles.disableEntity]: isDisabled()
        })}>
          <Text className={styles.assetName} lines={1} text={entity?.name} />
        </div>

        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>

        <div className={cx(styles.actions, styles.secondaryAction)}>
          <button onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }} className={cx(styles.selectIcon, {
            [styles.selectedEntity]: isSelected(),
            [styles.disableEntity]: isDisabled()
          })}>
            <i className={cx(styles.icon, {
              'paperflite-tick-selected': isSelected(),
              'paperflite-radio-button-unselected': !isSelected(),
              'paperflite-radio-button-selected': _.size(copyTo) === 0 && (moveTo && moveTo.id === entity.id),
            })}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <StreamBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} moveTo={moveTo} copyTo={copyTo} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const GroupRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection, moveTo, copyTo }) => {

  const location = useLocation();
  const pathName = _.get(location, "pathname");

  const sourceId = pathName.split('/').pop();

  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleExpand = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  const handleClick = () => {
    if(entity.id === sourceId) {
      return;
    } else {
      onSelection(entity);
    }
  }
  

  const isSelected = () => {
    if(copyTo && _.size(copyTo) > 0) {
      return copyTo.some((item) => item.id === entity.id);
    } else {
      return moveTo && moveTo.id === entity.id
    }
  }

  const isDisabled = () => {
    if(copyTo && _.size(copyTo) > 0){
      return copyTo.some((item) => item.id === sourceId);
    } else {
      return entity.id === sourceId
    }
  }


  return (
    <>
      <div className={cx(styles.entity)} style={{ width: width }} onClick={handleExpand}>
        <Tooltip placement="top" content="Source and destination cannot be the same" disabled={entity.id !== sourceId}> 
        <div className={cx(styles.name, {
           [styles.selectedEntity]: isSelected(),
           [styles.disableEntity]: isDisabled()
        })}>
          <div className={styles.entityNameWrapper}> 
            <Text text={entity?.name} />
          </div>
        </div>
        </Tooltip>

        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>

        <div className={cx(styles.actions, styles.secondaryAction)}>
          <button onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }} className={cx(styles.selectIcon, {
            [styles.selectedEntity]: isSelected(),
            [styles.disableEntity]: isDisabled()
          })}>
            <i className={cx(styles.icon, {
             'paperflite-tick-selected': isSelected(),
              'paperflite-radio-button-unselected': !isSelected(),
              'paperflite-radio-button-selected': _.size(copyTo) === 0 && (moveTo && moveTo.id === entity.id),
            })}></i>
          </button>
        </div>
      </div>

      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <animated.div style={childrenAnimation} className={styles.children}>
            <div ref={containerRef} className={styles.inner}>
              <GroupBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} moveTo={moveTo} copyTo={copyTo} />
            </div>
          </animated.div>
        )}
      </animated.div>
    </>
  );
}

export const StreamPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.placeholderName, styles.block)}></div>
    </div>
  );
}

export const AssetPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.placeholderName, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} /><span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
}

const GroupBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection, moveTo, copyTo }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.group[entity!.id!]?.loadingChildren,
      items: state.group[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchGroupChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser className={className} loading={loading} width={width} items={items} action={action} onSelection={onSelection} moveTo={moveTo} copyTo={copyTo} />
      </InfiniteScroll>
    </div>
  );
}

export { EntityBrowser, StreamsBrowser };